
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { WarehouseLocation } from '../api/types';

// Fix default marker icon
const defaultIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// Orange icon for warehouse markers
const warehouseIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

interface SearchResult {
    display_name: string;
    lat: string;
    lon: string;
}

interface LocationPickerProps {
    isOpen: boolean;
    title: string;
    onConfirm: (lat: number, lng: number) => void;
    onClose: () => void;
    initialLat?: number;
    initialLng?: number;
    warehouses?: Record<string, WarehouseLocation>;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
    isOpen,
    title,
    onConfirm,
    onClose,
    initialLat,
    initialLng,
    warehouses,
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);
    const [selectedLat, setSelectedLat] = useState<number | null>(initialLat ?? null);
    const [selectedLng, setSelectedLng] = useState<number | null>(initialLng ?? null);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const placeMarker = (lat: number, lng: number) => {
        const map = mapInstanceRef.current;
        if (!map) return;

        setSelectedLat(lat);
        setSelectedLng(lng);

        if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
        } else {
            const marker = L.marker([lat, lng], { draggable: true }).addTo(map);
            markerRef.current = marker;
            marker.on('dragend', () => {
                const pos = marker.getLatLng();
                setSelectedLat(pos.lat);
                setSelectedLng(pos.lng);
            });
        }

        map.setView([lat, lng], 12);
    };

    // Debounced search using Nominatim
    const handleSearchChange = (query: string) => {
        setSearchQuery(query);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (query.trim().length < 3) {
            setSearchResults([]);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const resp = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=5&email=dispatch@infinityhaul.com`,
                    {
                        headers: { 'Accept': 'application/json' },
                        referrerPolicy: 'no-referrer',
                    }
                );
                const data: SearchResult[] = await resp.json();
                setSearchResults(data);
            } catch {
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 400);
    };

    const handleSelectResult = (result: SearchResult) => {
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        placeMarker(lat, lng);
        setSearchQuery(result.display_name);
        setSearchResults([]);
    };

    useEffect(() => {
        if (!isOpen) {
            setSelectedLat(initialLat ?? null);
            setSelectedLng(initialLng ?? null);
            setSearchQuery('');
            setSearchResults([]);
            return;
        }
        if (!mapRef.current) return;

        const timer = setTimeout(() => {
            if (!mapRef.current) return;

            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }

            // Center on India
            const map = L.map(mapRef.current).setView([22.5937, 78.9629], 5);
            mapInstanceRef.current = map;

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                maxZoom: 19,
            }).addTo(map);

            // Add warehouse markers
            if (warehouses) {
                Object.entries(warehouses).forEach(([id, wh]) => {
                    L.marker([wh.latitude, wh.longitude], { icon: warehouseIcon })
                        .addTo(map)
                        .bindPopup(
                            `<div style="font-family: var(--ih-font-family-primary); padding: 2px 0;">
                                <strong style="font-size: 13px;">${wh.name}</strong><br/>
                                <span style="font-size: 11px; color: var(--ih-theme-text-muted);">${wh.latitude.toFixed(4)}, ${wh.longitude.toFixed(4)}</span>
                            </div>`
                        );
                });
            }

            // Place initial marker if coords exist
            if (initialLat && initialLng) {
                const marker = L.marker([initialLat, initialLng], { draggable: true }).addTo(map);
                markerRef.current = marker;
                setSelectedLat(initialLat);
                setSelectedLng(initialLng);
                map.setView([initialLat, initialLng], 8);

                marker.on('dragend', () => {
                    const pos = marker.getLatLng();
                    setSelectedLat(pos.lat);
                    setSelectedLng(pos.lng);
                });
            }

            // Click to place/move marker
            map.on('click', (e: L.LeafletMouseEvent) => {
                placeMarker(e.latlng.lat, e.latlng.lng);
            });

            setTimeout(() => map.invalidateSize(), 100);
        }, 50);

        return () => {
            clearTimeout(timer);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
            markerRef.current = null;
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (selectedLat !== null && selectedLng !== null) {
            onConfirm(selectedLat, selectedLng);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="rounded-2xl shadow-2xl w-[90vw] max-w-3xl h-[75vh] max-h-[600px] flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--ih-theme-bg-card)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4" style={{ borderBottomWidth: '1px', borderColor: 'var(--ih-theme-border-light)', backgroundColor: 'var(--ih-theme-bg-secondary)' }}>
                    <div>
                        <h2 className="text-lg font-bold" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-heading)' }}>{title}</h2>
                        <p className="text-xs" style={{ color: 'var(--ih-theme-text-tertiary)' }}>Search a place or click on the map to pin a location</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                        style={{ color: 'var(--ih-theme-text-muted)' }}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Map with Search Overlay */}
                <div className="flex-1 relative">
                    {/* Search Bar */}
                    <div className="absolute top-3 left-3 right-3 z-[1000]">
                        <div className="relative">
                            <div className="flex items-center rounded-lg shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-medium)' }}>
                                <svg className="w-4 h-4 ml-3 shrink-0" style={{ color: 'var(--ih-theme-text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    placeholder="Search city, address, or place..."
                                    className="w-full px-3 py-2.5 text-sm bg-transparent outline-none"
                                    style={{ color: 'var(--ih-theme-text-primary)' }}
                                />
                                {searching && (
                                    <div className="mr-3 shrink-0">
                                        <svg className="animate-spin h-4 w-4" style={{ color: 'var(--ih-theme-accent)' }} fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    </div>
                                )}
                            </div>

                            {/* Search Results Dropdown */}
                            {searchResults.length > 0 && (
                                <div className="mt-1 rounded-lg shadow-lg max-h-48 overflow-y-auto" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-medium)' }}>
                                    {searchResults.map((result, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleSelectResult(result)}
                                            className="w-full text-left px-4 py-2.5 text-sm transition-colors flex items-start gap-2"
                                            style={{ color: 'var(--ih-theme-text-secondary)', borderBottomWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}
                                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)')}
                                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        >
                                            <svg className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--ih-theme-text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <span className="line-clamp-2">{result.display_name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div ref={mapRef} className="w-full h-full" />
                </div>

                {/* Footer */}
                <div className="px-6 py-4 flex items-center justify-between" style={{ borderTopWidth: '1px', borderColor: 'var(--ih-theme-border-light)', backgroundColor: 'var(--ih-theme-bg-secondary)' }}>
                    <div className="text-sm" style={{ color: 'var(--ih-theme-text-secondary)' }}>
                        {selectedLat !== null && selectedLng !== null ? (
                            <span className="font-medium" style={{ fontFamily: 'var(--ih-font-family-mono)' }}>
                                📍 {selectedLat.toFixed(4)}, {selectedLng.toFixed(4)}
                            </span>
                        ) : (
                            <span style={{ color: 'var(--ih-theme-text-muted)' }}>No location selected</span>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                            style={{ color: 'var(--ih-theme-text-secondary)', backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-medium)' }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={selectedLat === null || selectedLng === null}
                            className="px-5 py-2 text-sm font-bold rounded-lg transition-all active:scale-95"
                            style={{
                                backgroundColor: (selectedLat !== null && selectedLng !== null) ? 'var(--ih-theme-accent)' : 'var(--ih-theme-bg-elevated)',
                                color: (selectedLat !== null && selectedLng !== null) ? 'var(--ih-theme-white)' : 'var(--ih-theme-text-muted)',
                                cursor: (selectedLat !== null && selectedLng !== null) ? 'pointer' : 'not-allowed',
                            }}
                        >
                            Confirm Location
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationPicker;
